import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Topbar from "../components/Topbar";
import CourseOutline from "../components/CourseOutline";
import ModuleMaterials from "../components/ModuleMaterials";
import { api, formatApiError, API_BASE, tokenStorage } from "../lib/api";
import { toast } from "sonner";
import LockedVideoPlayer from "../components/LockedVideoPlayer";
import {
  ArrowLeft, ChevronLeft, ChevronRight, CheckCircle2, Loader2, Lock,
  PanelLeftClose, PanelLeftOpen, ListChecks, X, ClipboardList,
} from "lucide-react";

export default function LessonPlayer() {
  const { id: courseId, lesson_id: lessonId } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startingTest, setStartingTest] = useState(false);
  // Riepilogo aperto su schermi larghi, a scomparsa sotto i 1024px: su tablet e
  // telefono il video deve avere tutta la larghezza disponibile.
  const [outlineOpen, setOutlineOpen] = useState(true);
  const [mobileOutline, setMobileOutline] = useState(false);
  // Percentuale vista aggiornata dal vivo dagli heartbeat del player, così la
  // barra di completamento si riempie mentre il video avanza (senza ricaricare).
  const [watchedPct, setWatchedPct] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/me/courses/${courseId}/progress`);
      setSummary(data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/studente");
    } finally {
      setLoading(false);
    }
  }, [courseId, navigate]);

  useEffect(() => { load(); }, [load]);

  // Cambiando lezione: chiudo il pannello mobile (altrimenti copre il video) e
  // azzero il progresso live, che riparte dal valore già visto della nuova lezione.
  useEffect(() => { setMobileOutline(false); setWatchedPct(0); }, [lessonId]);

  const startTest = async (moduleId) => {
    setStartingTest(true);
    try {
      const { data } = await api.post(`/tests/intermediate/${moduleId}/start`);
      navigate(`/studente/test/${data.attempt_id}`, { state: { start: data } });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        detail && typeof detail === "object"
          ? detail.message || formatApiError(err)
          : formatApiError(err)
      );
    } finally {
      setStartingTest(false);
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const res = await fetch(`${API_BASE}/attachments/${att.id}/download`, {
        headers: { Authorization: `Bearer ${tokenStorage.get()}` },
      });
      if (!res.ok) throw new Error("Download fallito");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Errore download");
    }
  };

  if (loading || !summary) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <Topbar />
        <div className="text-center py-16 text-brand-muted">
          <Loader2 size={20} className="animate-spin inline mr-2" /> Caricamento…
        </div>
      </div>
    );
  }

  const flat = [];
  summary.modules.forEach((m) => m.lessons.forEach((l) => flat.push({ ...l, module_id: m.id, module_title: m.title })));
  const idx = flat.findIndex((l) => l.id === lessonId);
  const lesson = flat[idx];
  const prev = idx > 0 ? flat[idx - 1] : null;
  const next = idx < flat.length - 1 ? flat[idx + 1] : null;
  const module = summary.modules.find((m) => m.id === lesson?.module_id);
  const moduleIndex = summary.modules.findIndex((m) => m.id === lesson?.module_id);

  if (!lesson) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <Topbar />
        <div className="text-center py-16 text-brand-muted">
          Lezione non trovata. <Link to={`/studente/corsi/${courseId}`} className="text-brand-blue underline">Torna al corso</Link>
        </div>
      </div>
    );
  }

  // Lezione non ancora sbloccata (per esempio raggiunta da un vecchio segnalibro):
  // spiegazione esplicita invece dell'errore generico del player.
  if (lesson.is_locked) {
    return (
      <div data-testid="lesson-locked-page" className="min-h-screen bg-brand-bg">
        <Topbar />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-sm bg-brand-blue/5 text-brand-blue mb-5">
            <Lock size={26} />
          </div>
          <h1 className="text-2xl text-brand-ink mb-2">Lezione bloccata</h1>
          <p className="text-brand-muted max-w-lg mx-auto mb-8">
            Per accedere a <strong>{lesson.title}</strong>:{" "}
            {lesson.lock_reason ||
              "completa le lezioni precedenti guardando ogni video per intero."}{" "}
            Il percorso va seguito nell’ordine previsto dal corso.
          </p>
          <Link
            to={`/studente/corsi/${courseId}`}
            data-testid="locked-back-to-course"
            className="btn-primary inline-flex items-center gap-2"
          >
            <ChevronLeft size={14} /> Torna al corso
          </Link>
        </main>
      </div>
    );
  }

  // ---- Qual è il passo successivo? --------------------------------------
  // L'azione a destra cambia in base al punto del percorso, perché ciò che
  // segue una lezione non è sempre un'altra lezione: può essere il test del
  // modulo, oppure il modulo successivo una volta superato il test.
  const isLastOfModule = module ? module.lessons[module.lessons.length - 1]?.id === lesson.id : false;
  const moduleHasTest = !!module?.has_intermediate_test;
  const testPassed = !!module?.intermediate_passed;
  const attemptsLeft = (module?.intermediate_attempts_max ?? 3) - (module?.intermediate_attempts_used ?? 0);

  // Il test si sblocca quando tutte le lezioni del modulo sono complete
  const moduleTestReady =
    moduleHasTest && module.intermediate_test_unlocked && !testPassed && !module.intermediate_locked;

  const nextModule = moduleIndex >= 0 ? summary.modules[moduleIndex + 1] : null;
  const nextModuleFirstLesson = nextModule?.lessons?.[0] || null;

  // "Passa al modulo successivo": modulo concluso (test superato o assente)
  const showNextModuleAction =
    isLastOfModule && (testPassed || !moduleHasTest) && !!nextModuleFirstLesson;

  const outline = (
    <CourseOutline
      summary={summary}
      currentLessonId={lessonId}
      courseId={courseId}
      onStartTest={startTest}
      startingTest={startingTest}
    />
  );

  // Percentuale mostrata dalla barra di completamento: il massimo tra il valore
  // già caricato e quello live degli heartbeat; 100 a lezione completata.
  const displayPct = lesson.is_completed
    ? 100
    : Math.min(100, Math.max(watchedPct, lesson.percent_watched || 0));

  return (
    <div data-testid="lesson-player-page" className="min-h-screen bg-brand-bg">
      <Topbar />

      <div className="flex">
        {/* Riepilogo del percorso — colonna fissa da 1024px in su.
            top-16 e calc(100vh-4rem): la Topbar è sticky e alta 4rem, quindi con
            `top-0 h-screen` la colonna sforerebbe di 64px sotto il viewport e il
            fondo dell'elenco resterebbe irraggiungibile. */}
        {outlineOpen && (
          <aside
            data-testid="course-outline-desktop"
            className="hidden lg:block w-[340px] shrink-0 border-r border-brand-line bg-white sticky top-16 h-[calc(100vh-4rem)] overflow-hidden"
          >
            {outline}
          </aside>
        )}

        {/* Riepilogo a scomparsa su schermi stretti */}
        {mobileOutline && (
          <div className="lg:hidden fixed inset-0 z-40 flex" data-testid="course-outline-mobile">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setMobileOutline(false)}
            />
            <div className="relative w-[85%] max-w-[340px] bg-white h-full shadow-xl overflow-hidden flex flex-col">
              <button
                onClick={() => setMobileOutline(false)}
                aria-label="Chiudi riepilogo"
                className="absolute top-3 right-3 z-10 text-white/80 hover:text-white"
              >
                <X size={18} />
              </button>
              {outline}
            </div>
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Barra di stato della lezione: si incolla sotto la Topbar, non sopra */}
          <div className="sticky top-16 z-20 bg-white px-4 sm:px-6 py-3 relative">
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setOutlineOpen((v) => !v)}
                data-testid="toggle-outline"
                title={outlineOpen ? "Nascondi il riepilogo" : "Mostra il riepilogo"}
                className="hidden lg:inline-flex items-center justify-center h-9 w-9 rounded-sm border border-brand-line text-brand-blue hover:border-brand-blue"
              >
                {outlineOpen ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
              </button>
              <button
                onClick={() => setMobileOutline(true)}
                data-testid="open-outline-mobile"
                className="lg:hidden inline-flex items-center gap-1.5 rounded-sm border border-brand-line text-brand-blue px-3 h-9 text-sm font-semibold"
              >
                <ListChecks size={15} /> Percorso
              </button>

              <button
                onClick={() => navigate(`/studente/corsi/${courseId}`)}
                data-testid="lesson-back-to-course"
                title="Torna al corso"
                className="inline-flex items-center gap-1.5 rounded-sm border border-brand-line text-brand-muted hover:text-brand-blue hover:border-brand-blue px-2.5 h-9 text-sm font-semibold transition-colors"
              >
                <ArrowLeft size={16} /> <span className="hidden md:inline">Torna al corso</span>
              </button>

              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest text-brand-gold truncate">
                  Modulo {moduleIndex + 1} · {module?.title}
                </div>
                <div className="text-sm font-semibold text-brand-ink truncate">
                  Lezione {idx + 1} di {flat.length}
                </div>
              </div>

              {lesson.is_completed ? (
                <span
                  data-testid="lesson-completed-badge"
                  className="inline-flex items-center gap-1.5 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1.5 text-xs font-semibold"
                >
                  <CheckCircle2 size={14} /> Lezione completata
                </span>
              ) : (
                <span
                  data-testid="lesson-progress-badge"
                  className="inline-flex items-center gap-1.5 rounded-sm bg-brand-bg border border-brand-line text-brand-muted px-3 py-1.5 text-xs"
                >
                  Visto {Math.round(displayPct)}% · richiesto 100%
                </span>
              )}
            </div>

            {/* Barra di completamento della lezione: la traccia fa da bordo
                inferiore e il riempimento cresce mentre il video avanza. */}
            <div
              className="absolute bottom-0 left-0 right-0 h-1 bg-brand-line"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(displayPct)}
              aria-label="Avanzamento della lezione"
            >
              <div
                data-testid="lesson-progress-bar"
                className={"h-full transition-[width] duration-300 ease-out " + (displayPct >= 100 ? "bg-emerald-500" : "bg-brand-blue")}
                style={{ width: `${displayPct}%` }}
              />
            </div>
          </div>

          <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-4">
              <h1 className="text-2xl sm:text-3xl text-brand-ink">{lesson.title}</h1>
              {lesson.description && <p className="text-brand-muted mt-1">{lesson.description}</p>}
            </div>

            <LockedVideoPlayer
              lesson={lesson}
              onProgress={(s) => {
                // Aggiorna la barra di completamento dal vivo, a ogni heartbeat.
                if (typeof s.percent === "number") setWatchedPct(s.percent);
                if (s.completed && !lesson.is_completed) {
                  // Ricarico il riepilogo: la lezione appena completata sblocca
                  // il passo successivo, che deve comparire subito nella colonna.
                  load();
                }
              }}
            />

            {/* Invito al test quando il modulo è concluso */}
            {moduleTestReady && (
              <div
                data-testid="module-test-cta"
                className="mt-8 bg-brand-blue text-white rounded-sm p-6 flex items-start justify-between gap-4 flex-wrap"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-gold mb-1.5">
                    <ClipboardList size={14} /> Verifica del modulo
                  </div>
                  <h2 className="text-xl mb-1">Hai completato le lezioni di questo modulo</h2>
                  <p className="text-white/80 text-sm max-w-lg">
                    Supera il test per sbloccare il modulo successivo. Hai a
                    disposizione {attemptsLeft} tentativi.
                  </p>
                </div>
                <button
                  onClick={() => startTest(module.id)}
                  disabled={startingTest}
                  data-testid="run-module-test-btn"
                  className="rounded-sm bg-brand-gold hover:bg-brand-gold-dark text-brand-ink font-semibold px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {startingTest ? <Loader2 size={14} className="animate-spin" /> : <ClipboardList size={14} />}
                  Esegui il test
                </button>
              </div>
            )}

            {/* Materiali del modulo — inline solo su schermi stretti; da xl in su
                vivono nella colonna destra fissa (vedi <aside> in fondo). */}
            {module?.attachments?.length > 0 && (
              <div className="mt-8 xl:hidden">
                <ModuleMaterials
                  attachments={module.attachments}
                  moduleTitle={module.title}
                  moduleIndex={moduleIndex}
                  onDownload={downloadAttachment}
                />
              </div>
            )}

            <div className="mt-8 flex justify-between gap-3">
              {prev ? (
                <button
                  onClick={() => navigate(`/studente/corsi/${courseId}/lezioni/${prev.id}`)}
                  data-testid="prev-lesson-btn"
                  className="inline-flex items-center gap-2 rounded-sm border border-brand-blue text-brand-blue hover:bg-brand-blue/5 px-4 py-2 font-semibold text-sm"
                >
                  <ChevronLeft size={14} /> Lezione precedente
                </button>
              ) : <div />}
              {/* L'azione a destra dipende dal punto del percorso. L'invito al test
                  NON compare qui: vive solo nel riquadro che appare sopra a video
                  completato, per non avere due comandi identici nella stessa pagina. */}
              {showNextModuleAction ? (
                <button
                  onClick={() =>
                    navigate(`/studente/corsi/${courseId}/lezioni/${nextModuleFirstLesson.id}`)
                  }
                  data-testid="next-module-btn"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Passa al modulo successivo <ChevronRight size={14} />
                </button>
              ) : next ? (
                next.is_locked ? (
                  <button
                    disabled
                    title={next.lock_reason || "Completa questa lezione per sbloccare la successiva"}
                    data-testid="next-lesson-locked-btn"
                    className="inline-flex items-center gap-2 rounded-sm bg-brand-line text-brand-muted px-4 py-2 font-semibold text-sm cursor-not-allowed"
                  >
                    <Lock size={14} /> Prossima lezione
                  </button>
                ) : (
                  <button
                    onClick={() => navigate(`/studente/corsi/${courseId}/lezioni/${next.id}`)}
                    data-testid="next-lesson-btn"
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    Prossima lezione <ChevronRight size={14} />
                  </button>
                )
              ) : (
                <button
                  onClick={() => navigate(`/studente/corsi/${courseId}`)}
                  data-testid="back-to-course-end-btn"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Torna al corso <ChevronRight size={14} />
                </button>
              )}
            </div>
          </main>
        </div>

        {/* Materiali del modulo — colonna destra fissa da 1280px in su. Compare
            solo se il modulo ha materiali, così non lascia una colonna vuota;
            sotto xl gli stessi materiali appaiono inline sotto il video. */}
        {module?.attachments?.length > 0 && (
          <aside
            data-testid="module-materials-desktop"
            className="hidden xl:block w-[320px] shrink-0 border-l border-brand-line bg-brand-bg sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto p-4"
          >
            <ModuleMaterials
              attachments={module.attachments}
              moduleTitle={module.title}
              moduleIndex={moduleIndex}
              onDownload={downloadAttachment}
            />
          </aside>
        )}
      </div>
    </div>
  );
}
